import { BasicModalHeader, ScrollableModal } from '@/components/widgets/scrollable-modal'
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { quickBetSettingModalStyle } from './quick-bet-setting-modal.style';
import { addCommasWithFix } from '@/utils/odds-utils';
import { useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter } from '@/navigation';
import { useGetAccountSetting } from '@/hooks/query/use-account-settings-data';
import { useBetslipContext } from '../betslip/contexts/betslip-context';
import { useEffect, useRef, useState } from 'react';
import { useAppearanceStore } from '@/store/appearance';
import { bettingInputMaxLength } from '@/hooks/query';
import { accountService } from '@/services/account.service';
import { routerService } from '@/services/router.service';
import { ViewEnum } from '@/types';
import { Icon } from '@/components/icons/svg-icons';
import { useBetslipStore } from '@/store/betslip';

export const QuickBetSettingModal = ({
	isOpen, 
	closeModal
}:{
    isOpen: boolean;
    closeModal: () => void;  
}) => {
	const t = useTranslations('General');
	const router = useRouter();
	const locale = useLocale();
	const pathName = usePathname();
	const { userSetting } = useGetAccountSetting();
	const { quickBetStake, currencyName } = useBetslipContext();
	const setIsQuickBet = useBetslipStore(state => state.setIsQuickBet);
	const queryClient = useQueryClient();
	const input = useRef<HTMLInputElement>(null);
	const [stake, setStake] = useState(quickBetStake); // -1: 未設定過, 0: 不可存
	const [isFocus, setIsFocus] = useState(false);
	const isDisabled = stake === quickBetStake || stake <= 0 || isNaN(stake);
	const view = useAppearanceStore(state => state.view);

	useEffect(() => {
		if (isOpen) {
			setStake(quickBetStake);
		}
	}, [quickBetStake, isOpen]);

	const [
		setFocusOddsKey,
	] = useBetslipStore(state => [
		state.setFocusOddsKey,
	]);

	const getDisplayStake = () => (stake === -1 ? '0' : stake ? addCommasWithFix(stake) : '');
	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const code = e.code;
		const isAlphaPressed = code.startsWith('Key');

		if (isAlphaPressed && !e.ctrlKey) {
			e.preventDefault();
			return;
		}

		const stakeLength = (stake ?? 0).toString().length;

		if (stakeLength >= bettingInputMaxLength) {
			setStake(0);
		}
	}
	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const input = e.target.value;
		const inputStake = parseInt(input.toString().replace(/[^0-9]/g, '')) || 0;
		setStake(inputStake);
	}
	const onClick = () => {
		if (!isDisabled) {
			setFocusOddsKey('');
			if (userSetting) {
				accountService.setQuickBetStake(queryClient, stake, userSetting);
				// WEB-13791: 第一次開啟快速投注，設定金額後沒有開啟快速投注模式
				// 改為設定quick bet stake後強制打開quick bet, 因為在每個情境之中, 都要打開quick bet才能設定 quick bet stake
				// 因此不影響其他情境
				setIsQuickBet(true);
				closeModal();
			}
		}
	}

	const onFocus = () => {
		setIsFocus(true);
		input.current?.select();
	}

	const onNumberPress = (value: string) => {
		if (stake <= 0 || (stake ?? 0).toString().length >= bettingInputMaxLength) {
			setStake(Number(value));
		} else {
			setStake((prev) => Number(prev.toString() + value));
		}
	};

	const onBackspacePress = () => {
		let stakeStr = (stake ?? 0).toString();
		stakeStr = stakeStr.substring(0, stakeStr.length - 1);
		const newStake = Number(stakeStr);
		setStake(isNaN(newStake) ? 0 : newStake);
	}

	const openHowToUse = () => {
		routerService.push(router.push, locale, '/how-to-use?tabName=quick-bet', pathName);
	}

	useEffect(() => {
		setStake(quickBetStake);
	}, [quickBetStake]);

	return (
		<ScrollableModal
			isOpen={isOpen}
			closeModal={closeModal}
			header={(
				<BasicModalHeader
					className={quickBetSettingModalStyle.header}
					title={view === ViewEnum.mars ? t('lbl_OneClickBet') : t('lbl_QuickBet')}
					closeModal={closeModal}
				/>
			)}
			footer={(
				<div className={quickBetSettingModalStyle.footer}>
					<button
						className={clsx('btn btn--primary', quickBetSettingModalStyle.betButton)}
						data-disabled={isDisabled}
						onClick={onClick}
					>
						<span className="btn__text">{t('lbl_Save')}</span>
					</button>
				</div>
			)}
		>
			<div className={quickBetSettingModalStyle.wrapper}>
				<div
					className={clsx('stake-input', quickBetSettingModalStyle.betInput)}
					data-selected={false}
					onClick={onFocus}
				>
					<div className="stake-input__stake">
						<div className="stake-input__textfield">
							<div className="stake-input__currency">{currencyName}</div>
							<div className={clsx('stake-input__input', quickBetSettingModalStyle.stakeInput)}>{getDisplayStake()}</div>
							<input
								ref={input}
								className={clsx('input', quickBetSettingModalStyle.input)}
								type="input"
								value={getDisplayStake()}
								onKeyDown={onKeyDown}
								onChange={onChange}
								onFocus={onFocus}
								onBlur={() => setIsFocus(false)}
							/>
						</div>
					</div>
				</div>
				<div className={clsx('bet-slip__keypad', quickBetSettingModalStyle.keypad)} data-show={isFocus}>
					<div className="keypad keypad--default-mini">
						<div className="keypad__numbers">
							{
								['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '00'].map(number => {
									return (
										<div
											key={number}
											className="keypad-btn"
											onClick={() => onNumberPress(number)}
										>
											{number}
										</div>
									)
								})
							}
							<div className="keypad__backspace" onClick={onBackspacePress}>
								<div className="keypad-btn">
									<Icon name="backspace" />
								</div>
							</div>
						</div>
					</div>
				</div> 
				<div className={clsx('bet-slip__info', quickBetSettingModalStyle.info)}>
					<ol>
						<li>
							<Icon name="view-my-bet" />
							{view === ViewEnum.galaxy ? t('lbl_QuickBetDescrip') : t('lbl_MarsQuickBetDescrip')}
						</li>
						{view === ViewEnum.galaxy ? 
							<li>
								{t('lbl_QuickBetEnabled')}
								<span>
									<button className="bet-slip__info-btn" onClick={openHowToUse}>
										{t('lbl_howToUse')}
										<Icon name="arrow-right" />
									</button>
								</span>
							
							</li> : null}
					</ol>
				</div>
			</div>
		</ScrollableModal>
	)
}